/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  message,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Spin,
  Alert,
  Descriptions,
  Modal,
  Divider,
  Statistic,
  Progress,
  Space,
  Typography,
  Tooltip,
  Badge,
  Timeline,
  Empty,
  Breadcrumb,
} from "antd";
import {
  ArrowLeftOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  PercentageOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  CopyOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { promoCodesApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";

// Initialize plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);

const { Title, Text } = Typography;

const PromoCodeDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);
        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions
  const canViewPromoCodes = hasPermission(userPermissions, "promocodes.view");
  const canEditPromoCodes = hasPermission(userPermissions, "promocodes.edit");
  const canDeletePromoCodes = hasPermission(
    userPermissions,
    "promocodes.delete",
  );

  useEffect(() => {
    if (!isCheckingPermission && canViewPromoCodes) {
      fetchPromoCode();
    }
  }, [id, canViewPromoCodes, isCheckingPermission]);

  const fetchPromoCode = async () => {
    try {
      setLoading(true);
      const data = await promoCodesApi.getById(id);
      setPromoCode(data);
    } catch (error) {
      console.error("Error fetching promo code:", error);
      message.error("Failed to load promo code details");
      history.push("/promo-codes");
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      setActionLoading(true);
      await promoCodesApi.deactivate(id);
      message.success("Promo code deactivated");
      setDeactivateModal(false);
      fetchPromoCode();
    } catch (error) {
      message.error("Failed to deactivate");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await promoCodesApi.remove(id);
      message.success("Promo code deleted");
      setDeleteModal(false);
      history.push("/promo-codes");
    } catch (error) {
      message.error("Failed to delete");
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Code copied!");
  };

  const getStatusInfo = () => {
    if (!promoCode)
      return {
        status: "Unknown",
        color: "default",
        icon: <InfoCircleOutlined />,
      };

    const now = dayjs();
    const start = dayjs(promoCode.validFrom);
    const end = dayjs(promoCode.validUntil);

    if (!promoCode.isActive)
      return {
        status: "Deactivated",
        color: "default",
        icon: <StopOutlined />,
      };
    if (now.isBefore(start))
      return {
        status: "Scheduled",
        color: "blue",
        icon: <ClockCircleOutlined />,
      };
    if (now.isAfter(end))
      return {
        status: "Expired",
        color: "error",
        icon: <CloseCircleOutlined />,
      };
    if (promoCode.usedCount >= promoCode.maxUses)
      return {
        status: "Exhausted",
        color: "warning",
        icon: <SafetyCertificateOutlined />,
      };

    return {
      status: "Active",
      color: "success",
      icon: <CheckCircleOutlined />,
    };
  };

  if (isCheckingPermission || loading) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div
            className="content container-fluid text-center"
            style={{ paddingTop: "100px" }}
          >
            <Spin size="large" tip="Loading code details..." />
          </div>
        </div>
      </div>
    );
  }

  if (!canViewPromoCodes || !promoCode) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Empty description="Access Denied or Not Found" />
          </div>
        </div>
      </div>
    );
  }

  const status = getStatusInfo();
  const usagePercent = Math.round(
    (promoCode.usedCount / promoCode.maxUses) * 100,
  );

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div
          className="content container-fluid"
          style={{ backgroundColor: "#f4f7fe", minHeight: "100vh" }}
        >
          {/* 1. Header Navigation */}
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <Space direction="vertical" size={0}>
              <Breadcrumb
                items={[
                  { title: "Admin" },
                  { title: "Promo Codes", href: "/promo-codes" },
                  { title: "Details" },
                ]}
              />
              <Title level={3} style={{ margin: "8px 0 0" }}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => history.goBack()}
                  style={{ marginRight: 8 }}
                />
                Promo Code:{" "}
                <Text code style={{ color: "#1890ff" }}>
                  {promoCode.code}
                </Text>
              </Title>
            </Space>
            <Space>
              {canEditPromoCodes &&
                promoCode.isActive &&
                status.status === "Active" && (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={() => setDeactivateModal(true)}
                  >
                    Deactivate
                  </Button>
                )}
              {canDeletePromoCodes && (
                <Button
                  danger
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteModal(true)}
                >
                  Delete
                </Button>
              )}
            </Space>
          </div>

          {/* 2. Key Metrics Row */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} className="shadow-sm">
                <Statistic
                  title="Current Status"
                  value={status.status}
                  valueStyle={{
                    color: status.color === "success" ? "#52c41a" : "#faad14",
                    fontSize: "20px",
                  }}
                  prefix={status.icon}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} className="shadow-sm">
                <Statistic
                  title="Discount Percentage"
                  value={promoCode.discountPercentage}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} className="shadow-sm">
                <Statistic
                  title="Redemptions"
                  value={promoCode.usedCount}
                  suffix={`/ ${promoCode.maxUses}`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card bordered={false} className="shadow-sm">
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Usage Limit Capacity
                </Text>
                <Progress
                  percent={usagePercent}
                  status={usagePercent >= 100 ? "exception" : "active"}
                  strokeColor={usagePercent >= 90 ? "#ff4d4f" : "#1890ff"}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            {/* 3. Detailed Information */}
            <Col xs={24} lg={16}>
              <Card
                title={
                  <span>
                    <InfoCircleOutlined /> Configuration Details
                  </span>
                }
                bordered={false}
                className="shadow-sm"
                style={{ borderRadius: "12px" }}
              >
                <Descriptions
                  bordered
                  column={1}
                  labelStyle={{
                    width: "30%",
                    fontWeight: "600",
                    background: "#fafafa",
                  }}
                >
                  <Descriptions.Item label="Generated Code">
                    <Space>
                      <Text
                        strong
                        style={{ fontSize: "18px", letterSpacing: "1px" }}
                      >
                        {promoCode.code}
                      </Text>
                      <Tooltip title="Copy Code">
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(promoCode.code)}
                        />
                      </Tooltip>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Code Prefix">
                    <Tag color="blue">{promoCode.prefix || "N/A"}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Maximum Uses">
                    {promoCode.maxUses} times
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Discount">
                    <Text type="success" strong>
                      {promoCode.discountPercentage}% OFF
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created Date">
                    {dayjs(promoCode.createdAt).format("DD MMMM YYYY, hh:mm A")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last Modified">
                    {dayjs(promoCode.updatedAt).fromNow()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            {/* 4. Timeline & Context */}
            <Col xs={24} lg={8}>
              <Card
                title={
                  <span>
                    <CalendarOutlined /> Validity Timeline
                  </span>
                }
                bordered={false}
                className="shadow-sm mb-4"
                style={{ borderRadius: "12px" }}
              >
                <Timeline
                  items={[
                    {
                      color: "green",
                      children: (
                        <>
                          <Text type="secondary">Becomes Active</Text>
                          <div style={{ fontWeight: "500" }}>
                            {dayjs(promoCode.validFrom).format("MMM DD, YYYY")}
                          </div>
                          <Text style={{ fontSize: "12px" }} type="secondary">
                            {dayjs(promoCode.validFrom).format("hh:mm A")}
                          </Text>
                        </>
                      ),
                    },
                    {
                      color: dayjs().isAfter(dayjs(promoCode.validUntil))
                        ? "red"
                        : "gray",
                      children: (
                        <>
                          <Text type="secondary">Expires On</Text>
                          <div style={{ fontWeight: "500" }}>
                            {dayjs(promoCode.validUntil).format("MMM DD, YYYY")}
                          </div>
                          <Text style={{ fontSize: "12px" }} type="secondary">
                            {dayjs(promoCode.validUntil).format("hh:mm A")}
                          </Text>
                        </>
                      ),
                    },
                  ]}
                />

                <Divider dashed />

                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    backgroundColor: "#e6f7ff",
                    borderRadius: "8px",
                  }}
                >
                  <Space direction="vertical" size={2}>
                    <GiftOutlined
                      style={{ fontSize: "24px", color: "#1890ff" }}
                    />
                    <Text strong>Promotion Lifetime</Text>
                    <Text type="secondary">
                      Valid for{" "}
                      {dayjs(promoCode.validUntil).diff(
                        dayjs(promoCode.validFrom),
                        "days",
                      )}{" "}
                      days
                    </Text>
                  </Space>
                </div>
              </Card>

              {/* Action Alert Context */}
              {status.status === "Expired" && (
                <Alert
                  message="Expired Promo"
                  description="This code has reached its end date and is no longer valid for customers."
                  type="error"
                  showIcon
                />
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* Deactivate Modal */}
      <Modal
        title="Deactivate Promo Code"
        open={deactivateModal}
        onOk={handleDeactivate}
        onCancel={() => setDeactivateModal(false)}
        confirmLoading={actionLoading}
        okText="Confirm Deactivation"
        okType="danger"
      >
        <p>
          Are you sure you want to deactivate <b>{promoCode.code}</b>? It will
          immediately stop working for all users.
        </p>
      </Modal>

      {/* Delete Modal */}
      <Modal
        title="Delete Promo Code"
        open={deleteModal}
        onOk={handleDelete}
        onCancel={() => setDeleteModal(false)}
        confirmLoading={actionLoading}
        okText="Permanently Delete"
        okType="danger"
      >
        <Alert
          type="warning"
          showIcon
          message="Irreversible Action"
          description="Deleting this code will remove all history associated with its configuration. Active bookings using this code will not be affected."
        />
      </Modal>
    </div>
  );
};

export default PromoCodeDetails;
